import { Chart, type ChartType, type Plugin } from 'chart.js';

import type { MetricBarLabelsOptions } from '../generic/chartPluginOptions';

declare module 'chart.js' {
    interface PluginOptionsByType<TType extends ChartType> {
        metricBarLabels?: MetricBarLabelsOptions;
    }
}

const FONT_SIZE = 12;
/** Gap between the top of a bar and a label inside it. */
const INSIDE_TOP_PADDING = 12;
/** Gap between the top of a bar and a label above it. */
const ABOVE_BAR_GAP = 6;
/** A bar shorter than this cannot hold its label, which goes above it instead. */
const MIN_HEIGHT_FOR_INSIDE_LABEL = 20;

/** The drawn position of a bar, read without trusting the element's loose prop types. */
function barGeometry(element: {
    getProps: (props: string[], final?: boolean) => unknown;
}): { x: number; top: number; height: number } | null {
    const props = element.getProps(['x', 'y', 'base'], true);
    if (typeof props !== 'object' || props === null) return null;
    const x: unknown = Reflect.get(props, 'x');
    const y: unknown = Reflect.get(props, 'y');
    const base: unknown = Reflect.get(props, 'base');
    if (typeof x !== 'number' || typeof y !== 'number' || typeof base !== 'number') return null;
    return { x, top: Math.min(y, base), height: Math.abs(y - base) };
}

/**
 * Draws `MetricSeriesSpec.barLabels` on their bars: inside the top of a bar tall enough to
 * hold the text, in the ink readable on that bar's fill, otherwise just above it in the ink
 * readable on the chart surface. Inert unless the chart's options carry `metricBarLabels`,
 * which `buildMetricChartOptions` adds only for a spec with labels.
 */
/** Chart.js hands a plugin its resolved options untyped; ours come from `buildBarLabelOptions`. */
function isBarLabelsOptions(
    options: Record<string, unknown> | undefined
): options is Record<string, unknown> & MetricBarLabelsOptions {
    return (
        options !== undefined && Array.isArray(options.sets) && typeof options.aboveInk === 'string'
    );
}

export const metricBarLabelsPlugin: Plugin<'bar'> = {
    id: 'metricBarLabels',
    afterDatasetsDraw(chart, _args, options: Record<string, unknown> | undefined) {
        if (!isBarLabelsOptions(options) || options.sets.length === 0) return;
        const { ctx } = chart;
        ctx.save();
        ctx.font = `${FONT_SIZE}px ${Chart.defaults.font.family ?? 'sans-serif'}`;
        ctx.textAlign = 'center';

        for (const set of options.sets) {
            const meta = chart.getDatasetMeta(set.datasetIndex);
            if (!meta.visible) continue;
            meta.data.forEach((element, index) => {
                const label = set.labels[index];
                const bar = label == null ? null : barGeometry(element);
                if (label == null || !bar) return;
                if (bar.height < MIN_HEIGHT_FOR_INSIDE_LABEL) {
                    ctx.fillStyle = options.aboveInk;
                    ctx.textBaseline = 'bottom';
                    ctx.fillText(label, bar.x, bar.top - ABOVE_BAR_GAP);
                } else {
                    ctx.fillStyle = set.insideInk[index] ?? options.aboveInk;
                    ctx.textBaseline = 'top';
                    ctx.fillText(label, bar.x, bar.top + INSIDE_TOP_PADDING);
                }
            });
        }

        ctx.restore();
    },
};
